import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { contrastText } from "./color";
import { fullName, sortByName } from "./players";
import { FORMATION_DRAG_TYPE, } from "./types";
const ROW_BG = "rgba(15, 23, 42, 0.04)";
const ROW_BG_HOVER = "rgba(15, 23, 42, 0.07)";
const TEXT_PRIMARY = "#0f172a";
const TEXT_SECONDARY = "rgba(15, 23, 42, 0.65)";
const EMPTY_TEXT = "Bench is empty";
const DEFAULT_HEADING = "Bench";
const BenchPlayerItem = ({ player, primaryColour, isSelected = false, onClick, onSwapWithPitchPlayer, }) => {
    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: FORMATION_DRAG_TYPE,
        item: { source: "bench", player },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }), [player]);
    const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
        accept: FORMATION_DRAG_TYPE,
        canDrop: (item) => item.source === "pitch",
        drop: (item) => {
            if (item.source === "pitch" && onSwapWithPitchPlayer) {
                onSwapWithPitchPlayer(item.slotId, player);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }), [player, onSwapWithPitchPlayer]);
    const setRefs = (node) => {
        dragRef(node);
        dropRef(node);
    };
    const numberTextColour = contrastText(primaryColour);
    const isDropTarget = isOver && canDrop;
    return (_jsxs(Box, { ref: setRefs, "data-testid": `bench-player-${player.id}`, role: "button", "aria-pressed": isSelected, "aria-label": `Bench: #${player.squadNumber ?? "?"} ${fullName(player)}${player.primaryPosition ? ` (${player.primaryPosition})` : ""}`, onClick: (e) => {
            e.stopPropagation();
            onClick?.(player);
        }, sx: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 0,
            pr: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: ROW_BG,
            borderLeft: `3px solid ${primaryColour}`,
            color: TEXT_PRIMARY,
            cursor: "grab",
            opacity: isDragging ? 0.3 : 1,
            userSelect: "none",
            outline: isSelected
                ? `2px solid ${primaryColour}`
                : isDropTarget
                    ? `2px dashed ${primaryColour}`
                    : "none",
            outlineOffset: 2,
            transition: "background-color 0.1s ease, outline 0.1s ease",
            "&:hover": {
                backgroundColor: ROW_BG_HOVER,
            },
        }, children: [_jsx(Box, { sx: {
                    ml: 1,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: primaryColour,
                    border: "1.5px solid rgba(15, 23, 42, 0.55)",
                    color: numberTextColour,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    flexShrink: 0,
                }, children: player.squadNumber ?? "-" }), _jsx(Typography, { noWrap: true, sx: {
                    flex: 1,
                    fontWeight: 500,
                    fontSize: "0.9rem",
                }, children: fullName(player) }), player.primaryPosition && (_jsx(Box, { sx: {
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.75,
                    backgroundColor: "rgba(15, 23, 42, 0.06)",
                    border: "1px solid rgba(15, 23, 42, 0.12)",
                    color: TEXT_SECONDARY,
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    lineHeight: 1,
                    flexShrink: 0,
                }, children: player.primaryPosition }))] }));
};
export const Bench = ({ players, primaryColour, onDrop, onSwapBenchPlayer, heading = DEFAULT_HEADING, selectedPlayerId = null, onClickPlayer, onClickArea, }) => {
    const sorted = useMemo(() => sortByName(players), [players]);
    const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
        accept: FORMATION_DRAG_TYPE,
        canDrop: (item) => item.source !== "bench",
        drop: (item, monitor) => {
            // If a BenchPlayerItem already handled the drop (swap), skip the
            // outer remove-to-bench behaviour.
            if (monitor.didDrop())
                return;
            onDrop(item);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }), [onDrop]);
    const isHighlighted = isOver && canDrop;
    return (_jsxs(Box, { ref: dropRef, "data-testid": "formation-bench", onClick: () => onClickArea?.(), sx: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: "transparent",
            p: 1.5,
            gap: 1,
            outline: isHighlighted ? `2px dashed ${primaryColour}` : "none",
            outlineOffset: "-4px",
            transition: "outline 0.1s ease",
            width: "100%",
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            cursor: onClickArea ? "pointer" : "default",
        }, children: [_jsx(Typography, { component: "h1", sx: {
                    color: TEXT_PRIMARY,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    lineHeight: 1.2,
                    mb: 0.5,
                    flexShrink: 0,
                }, children: heading }), sorted.length === 0 ? (_jsx(Typography, { sx: {
                    color: TEXT_SECONDARY,
                    fontStyle: "italic",
                    fontSize: "0.85rem",
                }, children: EMPTY_TEXT })) : (_jsx(Stack, { spacing: 0.75, sx: {
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    // Slim, theme-aware scrollbar so it doesn't dominate the column.
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(15,23,42,0.25) transparent",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(15,23,42,0.25)",
                        borderRadius: 3,
                    },
                    pr: 0.5,
                }, children: sorted.map((p) => (_jsx(BenchPlayerItem, { player: p, primaryColour: primaryColour, isSelected: selectedPlayerId === p.id, onClick: onClickPlayer, onSwapWithPitchPlayer: onSwapBenchPlayer }, p.id))) }))] }));
};
